import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageCircle, BellRing, BellOff } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo, REACTIONS } from "@/lib/constants";
import { toast } from "sonner";

const LAST_SEEN_KEY = "twatter_last_notif_id";

function reactionEmoji(rid) {
  return REACTIONS.find((r) => r.id === rid)?.emoji || "🔥";
}

function fireDesktopNotification(n) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  const title = n.kind === "comment"
    ? `${n.actor_handle} replied to your vent`
    : `${n.actor_handle} reacted ${reactionEmoji(n.preview)}`;
  const body = n.kind === "comment" ? `"${n.preview}"` : "Someone feels you.";
  try {
    const notif = new Notification("twatter", { body: `${title}\n${body}`, tag: n.id, silent: false });
    notif.onclick = () => {
      window.focus();
      window.location.href = `/vent/${n.post_id}`;
      notif.close();
    };
  } catch (e) {
    /* silent */
  }
}

export default function NotificationsBell({ user }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async ({ maybePush = false } = {}) => {
    try {
      const { data: resp } = await api.get(`/users/${user.id}/notifications`, { params: { limit: 30 } });
      setData(resp);
      if (maybePush && perm === "granted" && resp.items.length) {
        const lastSeen = localStorage.getItem(LAST_SEEN_KEY);
        const freshest = resp.items[0].id;
        // fire push for all items newer than lastSeen
        if (lastSeen && lastSeen !== freshest) {
          const idx = resp.items.findIndex((n) => n.id === lastSeen);
          const fresh = idx === -1 ? resp.items : resp.items.slice(0, idx);
          fresh.slice(0, 3).forEach(fireDesktopNotification);
        }
        localStorage.setItem(LAST_SEEN_KEY, freshest);
      } else if (resp.items.length && !localStorage.getItem(LAST_SEEN_KEY)) {
        localStorage.setItem(LAST_SEEN_KEY, resp.items[0].id);
      }
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load({ maybePush: true }), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user.id, perm]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      await api.post(`/users/${user.id}/notifications/mark-read`);
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  const askPermission = async () => {
    if (!("Notification" in window)) {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted") toast.success("Browser notifications ON.");
    else if (p === "denied") toast.error("Notifications blocked. Enable them in your browser settings.");
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        data-testid="notifications-bell"
        className="relative w-9 h-9 rounded-full flex items-center justify-center border transition-colors"
        style={{ borderColor: "var(--exhale-border)", background: "var(--exhale-surface)", color: "var(--exhale-text-2)" }}
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span
            data-testid="notifications-unread-dot"
            className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full"
            style={{ background: "var(--exhale-accent)", boxShadow: "0 0 0 2px var(--exhale-surface)" }}
          />
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          className="absolute right-0 mt-2 w-80 sm:w-96 rounded-2xl border shadow-2xl z-50 overflow-hidden"
          style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
        >
          <div className="p-4 border-b flex items-center justify-between gap-2" style={{ borderColor: "var(--exhale-border)" }}>
            <h4 className="font-display text-sm font-semibold" style={{ color: "var(--exhale-text)" }}>
              Notifications
            </h4>
            {perm === "granted" ? (
              <span
                data-testid="push-enabled-badge"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 rounded-full"
                style={{ background: "rgba(52, 199, 89, 0.12)", color: "#34C759", border: "1px solid rgba(52, 199, 89, 0.3)" }}
              >
                <BellRing className="w-3 h-3" /> Push on
              </span>
            ) : perm === "denied" ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 rounded-full"
                style={{ background: "var(--exhale-badge)", color: "var(--exhale-text-muted)", border: "1px solid var(--exhale-border)" }}
              >
                <BellOff className="w-3 h-3" /> Blocked
              </span>
            ) : (
              <button
                onClick={askPermission}
                data-testid="enable-push-btn"
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 rounded-full transition-colors"
                style={{ background: "var(--exhale-accent)", color: "#0C0B0E" }}
              >
                <BellRing className="w-3 h-3" /> Enable push
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto" data-testid="notifications-list">
            {data.items.length === 0 ? (
              <div className="p-6 text-center text-sm" style={{ color: "var(--exhale-text-muted)" }}>
                Silence. No one has responded to your vents yet.
              </div>
            ) : (
              data.items.map((n) => {
                const Icon = n.kind === "comment" ? MessageCircle : Heart;
                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      setOpen(false);
                      navigate(`/vent/${n.post_id}`);
                    }}
                    data-testid={`notification-${n.id}`}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 border-b transition-colors hover:bg-[color:var(--exhale-surface-hover)]"
                    style={{ borderColor: "var(--exhale-border)" }}
                  >
                    <Icon
                      className="w-4 h-4 mt-0.5 shrink-0"
                      style={{ color: n.kind === "comment" ? "var(--exhale-calm)" : "var(--exhale-accent)" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm" style={{ color: "var(--exhale-text)" }}>
                        <span className="font-semibold">{n.actor_handle}</span>{" "}
                        {n.kind === "comment" ? "replied to your vent" : `reacted ${reactionEmoji(n.preview)}`}
                      </div>
                      {n.kind === "comment" && n.preview && (
                        <div className="text-xs mt-0.5 truncate" style={{ color: "var(--exhale-text-muted)" }}>
                          &quot;{n.preview}&quot;
                        </div>
                      )}
                      <div className="text-[10px] font-mono uppercase mt-1" style={{ color: "var(--exhale-text-muted)" }}>
                        {timeAgo(n.created_at)}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
